import {
  GRANT_CLOSED_MODES,
  GRANT_SORTS,
  REGIONS,
  type GrantClosedMode,
  type GrantSort,
} from '@zoxo/shared';

/**
 * 목록 상태(정렬·지역·진행중)를 **어디서나 같게** 유지하는 규칙.
 *
 * 1. **URL 이 이긴다.** `?sort=&region=&closed=` 가 있으면 그것. 링크 공유가 되는 이유다.
 * 2. **URL 에 없으면 쿠키.** 마지막 선택을 `zx_list` 쿠키에 둔다. 로그인 없이도, 서버(SSR)도
 *    읽으므로 첫 화면부터 그 상태로 그려진다 — 깜빡임이 없다.
 * 3. 둘 다 없으면 기본(최신순 · 전체 지역 · 진행중만).
 *
 * 유형 탭 링크는 현재 상태를 URL 에 실어 나른다(`carrySearch`). 검색어(q)는 탭 안에서만
 * 뜻이 있어 안 나른다.
 *
 * 계산은 목록 loader(list-loader.ts) 한 곳이다 — 서버와 브라우저가 같은 함수로 같은 값을 낸다.
 */
export const LIST_COOKIE = 'zx_list';

/** 쿠키에 적힌 마지막 선택. 값이 있는 키만 이긴다. region '' = 전체 지역(명시적). */
export interface ListMemory {
  sort?: GrantSort;
  region?: string;
  closed?: GrantClosedMode;
}

export interface ListState {
  sort: GrantSort;
  region?: string;
  closed: GrantClosedMode;
}

const isSort = (v: string): v is GrantSort => (GRANT_SORTS as readonly string[]).includes(v);
const isClosed = (v: string): v is GrantClosedMode => (GRANT_CLOSED_MODES as readonly string[]).includes(v);
const isRegion = (v: string): boolean => v === '' || (REGIONS as readonly string[]).includes(v);

/** `Cookie:` 헤더(또는 document.cookie)에서 zx_list 를 읽는다. 없거나 깨졌으면 빈 객체. */
export function parseListCookie(cookieHeader: string | null | undefined): ListMemory {
  if (!cookieHeader) return {};
  for (const part of cookieHeader.split(';')) {
    const [k, ...rest] = part.trim().split('=');
    if (k !== LIST_COOKIE) continue;
    let p: URLSearchParams;
    try {
      p = new URLSearchParams(decodeURIComponent(rest.join('=')));
    } catch {
      return {};
    }
    const m: ListMemory = {};
    const sort = p.get('sort');
    if (sort !== null && isSort(sort)) m.sort = sort;
    const region = p.get('region');
    if (region !== null && isRegion(region)) m.region = region;
    const closed = p.get('closed');
    if (closed !== null && isClosed(closed)) m.closed = closed;
    return m;
  }
  return {};
}

/** URL 값 → 쿠키 값 → 기본값 순으로 유효 상태를 정한다. */
export function resolveListState(
  url: { sort?: GrantSort; region?: string; closed?: GrantClosedMode },
  memory: ListMemory,
): ListState {
  const region = url.region ?? (memory.region !== undefined ? memory.region || undefined : undefined);
  return {
    sort: url.sort ?? memory.sort ?? 'latest',
    region,
    closed: url.closed ?? memory.closed ?? 'exclude',
  };
}

/** 유형 탭 링크에 실을 검색 파라미터 — 기본값은 뺀다(주소가 짧고, /news 와 같은 캐시 키). */
export function carrySearch(s: { sort?: GrantSort; region?: string; closed?: GrantClosedMode }): Record<string, string> {
  const out: Record<string, string> = {};
  if (s.region) out.region = s.region;
  if (s.closed && s.closed !== 'exclude') out.closed = s.closed;
  if (s.sort && s.sort !== 'latest') out.sort = s.sort;
  return out;
}

/** 선택이 바뀔 때마다 세 값을 **명시적으로** 적는다 — 「전체 지역」도 region='' 로 남겨야 이전 지역이 되살아나지 않는다. */
export function writeListCookie(s: { sort?: GrantSort; region?: string; closed?: GrantClosedMode }): void {
  if (typeof document === 'undefined') return;
  const p = new URLSearchParams({ sort: s.sort ?? 'latest', region: s.region ?? '', closed: s.closed ?? 'exclude' });
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  try {
    document.cookie = `${LIST_COOKIE}=${encodeURIComponent(p.toString())}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
  } catch {
    /* 쿠키 차단 — URL 만으로 동작한다. */
  }
}

export const CLOSED_LABEL: Record<GrantClosedMode, string> = {
  exclude: '진행중만',
  include: '마감 포함',
  only: '마감만',
};

export const SORT_LABEL: Record<GrantSort, string> = {
  latest: '최신순',
  closing: '마감임박순',
  amount: '금액순',
};
